import { SearchFormType } from './search-form-type.model';
import { LatLon } from '../../util/geodesy/latlon';
import { QueryValidationReason } from './query-validation-result.model';
import { AirportService, MAX_RADIUS, MIN_RADIUS } from '../../airport/airport.service';
import { BooleanFilter, FilterKeys } from '../filters/filters';
import { ApproachFilter, FuelFilter, RunwayLengthFilter, ServiceFilter, ServiceType } from '../filters/airport-filters';
import { isNullOrUndefined, isNumeric } from '../../util/helpers';
import { ApproachType } from '../../airport/models/approach-type.model';
import { FuelType } from '../../business/models/business-fuel.model';
import { ActivityType } from '../filters/activity-filters';
import { BoundingBox } from '../../util/geodesy/bounding-box';
import { AviationEventFilter } from '../../shared/models/aviation-event-filter.model';
import { FlyingClubFinderFilter } from '../../shared/models/flying-club-finder-filter.model';
import { FlightSchoolFinderFilter } from '../../shared/models/flight-school-finder-filter.model';
import { UniversityFinderFilter } from '../../shared/models/university-finder-filter.model';
import { FboFinderFilter } from '../../shared/models/fbo-finder-filter.model';
import { ServiceRepairFinderFilter } from '../../shared/models/service-repair-finder-filter.model';
import { RentalSalesFinderFilter } from '../../shared/models/rental-sales-finder-filter.model';
import * as moment from 'moment-timezone';
/**
 * Regex used for parsing a latitude or a longitude value in decimal degrees (e.g., "-123.4567")
 * @type {RegExp}
 */
export var LATLON_REGEX = /^-?[0-9]+\.[0-9]+$/;
/**
 * Search query model for airports and destinations.
 */
var SearchQuery = /** @class */ (function () {
    function SearchQuery() {
        var _this = this;
        /**
         * Search type
         */
        this.type = SearchFormType.Airports;
        /**
         * Radius (nautical miles)
         */
        this.radius = 50;
        /**
         * Activity type to search for (only relevant for search type of Activities).
         */
        this.activity = ActivityType.Restaurants;
        /**
         * Search result filters
         */
        this.filters = [
            new BooleanFilter({
                key: FilterKeys.PublicUse,
                enabled: true,
                apply: function (airport) { return airport.publicUse; }
            }),
            new BooleanFilter({
                key: FilterKeys.Towered,
                apply: function (airport) { return airport.towered; }
            }),
            new BooleanFilter({
                key: FilterKeys.NoLandingFee,
                apply: function (airport) { return !airport.landingFee; }
            }),
            new BooleanFilter({
                key: FilterKeys.CourtesyCar,
                apply: function (airport) { return airport.courtesyCar; }
            }),
            new BooleanFilter({
                key: FilterKeys.RentalCar,
                apply: function (airport) { return airport.rentalCar; }
            }),
            new BooleanFilter({
                key: FilterKeys.Customs,
                apply: function (airport) { return airport.customsIntlEntry; }
            }),
            new BooleanFilter({
                key: FilterKeys.Oxygen,
                apply: function (airport) { return airport.oxygen; }
            }),
            new BooleanFilter({
                key: FilterKeys.Restaurant,
                apply: function (airport) { return airport.restaurantOnField; }
            }),
            new BooleanFilter({
                key: FilterKeys.Camping,
                apply: function (airport) { return airport.campingOnField; }
            }),
            new BooleanFilter({
                key: FilterKeys.PavedRunway,
                apply: function (airport) { return airport.paved; }
            }),
            new BooleanFilter({
                key: FilterKeys.Grass,
                apply: function (airport) { return airport.grass; }
            }),
            new BooleanFilter({
                key: FilterKeys.Helipad,
                apply: function (airport) { return airport.helipad; }
            }),
            new BooleanFilter({
                key: FilterKeys.Waterway,
                apply: function (airport) { return airport.waterway; }
            }),
            new RunwayLengthFilter(),
            new FuelFilter(FuelType._100LL),
            new FuelFilter(FuelType.JetA, [FuelType.JetAPremix]),
            new FuelFilter(FuelType.MOGAS),
            new FuelFilter(FuelType.UL94),
            new ApproachFilter(ApproachType.ILS),
            new ApproachFilter(ApproachType.RNAV),
            new ApproachFilter(ApproachType.VOR),
            new ServiceFilter(ServiceType.AirframeRepair),
            new ServiceFilter(ServiceType.EngineRepair),
            new ServiceFilter(ServiceType.AvionicsRepair)
        ];
        /**
        * Filter map that allows looking up a specific filter (or array of filters, in case of the multi flag being true)
        * for a given filter key. This property is initialized in the constructor.
        */
        this._filterMap = new Map();
        this.filters.forEach(function (f) {
            if (f.multi) {
                if (_this._filterMap.has(f.key)) {
                    _this._filterMap.get(f.key).push(f);
                }
                else {
                    _this._filterMap.set(f.key, [f]);
                }
            }
            else {
                _this._filterMap.set(f.key, f);
            }
        });
    }
    Object.defineProperty(SearchQuery.prototype, "sanitizedTerm", {
        /**
         * Returns the sanitized query text with all invalid characters removed. This is what will be sent to the API.
         * @returns {string}
         */
        get: function () {
            return AirportService.sanitizeSearchTerm(this.term);
        },
        enumerable: true,
        configurable: true
    });
    SearchQuery.formatLatLon = function (loc) {
        var lat = loc.lat.toFixed(7);
        var lon = loc.lon.toFixed(7);
        return lat + "," + lon;
    };
    SearchQuery.parseLatLon = function (loc) {
        if (!loc) {
            return null;
        }
        var parts = loc.split(',');
        if (parts.length !== 2) {
            return null;
        }
        var latstr = parts[0];
        var lonstr = parts[1];
        if (LATLON_REGEX.test(latstr) && LATLON_REGEX.test(lonstr)) {
            try {
                return new LatLon(+latstr, +lonstr);
            }
            catch (e) {
                console.warn('Error parsing LatLon:', e);
                return null;
            }
        }
        return null;
    };
    /**
     * Parses a separator-delimited parameter value into an array of trimmed, non-empty strings. Defaults to a
     * comma; callers whose values may themselves contain commas (e.g. the FBO fuel-provider tag names) pass a
     * different separator such as '|'.
     */
    SearchQuery.parseList = function (value, separator) {
        if (separator === void 0) { separator = ','; }
        if (!value) {
            return [];
        }
        return value.split(separator).map(function (s) { return s.trim(); }).filter(function (s) { return s.length > 0; });
    };
    SearchQuery.getBooleanValue = function (params, paramName) {
        if (params.has(paramName)) {
            if (params.get(paramName) === 'true') {
                return true;
            }
            else {
                return false;
            }
        }
        else {
            return false;
        }
    };
    /**
     * Decodes the filters from the given parameter map.
     * @param {ParamMap} params
     */
    SearchQuery.decode = function (params) {
        var query = new SearchQuery();
        query.term = params.get('q');
        if (params.has('loc') && params.has('radius') && isNumeric(params.get('radius'))) {
            query.location = SearchQuery.parseLatLon(params.get('loc'));
            query.radius = +params.get('radius');
            if (params.has('locstr')) {
                query.locationDescription = params.get('locstr');
            }
        }
        else if (params.has('ne') && params.has('sw')) {
            var ne = SearchQuery.parseLatLon(params.get('ne'));
            var sw = SearchQuery.parseLatLon(params.get('sw'));
            if (ne && sw) {
                query.bounds = new BoundingBox(ne, sw);
            }
        }
        else if (params.has('state')) {
            if (params.has('locstr')) {
                query.locationDescription = params.get('locstr');
            }
            query.state = params.get('state');
        }
        if (params.get('t') === 'activities') {
            query.type = SearchFormType.Activities;
            if (params.has('activity')) {
                query.activity = params.get('activity');
            }
        }
        if (params.get('t') === 'events') {
            query.type = SearchFormType.AviationEvents;
            query.aviationEventsFilter = new AviationEventFilter();
            query.aviationEventsFilter.airshow = SearchQuery.getBooleanValue(params, 'airshow');
            query.aviationEventsFilter.airshowwithmilitarydemonstrationteam = SearchQuery.getBooleanValue(params, 'airshowwithMilitaryDemonstrationTeam');
            query.aviationEventsFilter.flyin = SearchQuery.getBooleanValue(params, 'flyin');
            query.aviationEventsFilter.flyout = SearchQuery.getBooleanValue(params, 'flyout');
            query.aviationEventsFilter.pancakeBreakfast = SearchQuery.getBooleanValue(params, 'pancakeBreakfast');
            query.aviationEventsFilter.socialGathering = SearchQuery.getBooleanValue(params, 'socialGathering');
            query.aviationEventsFilter.openhouse = SearchQuery.getBooleanValue(params, 'openhouse');
            query.aviationEventsFilter.pokerRun = SearchQuery.getBooleanValue(params, 'pokerRun');
            query.aviationEventsFilter.aviationCommunityDay = SearchQuery.getBooleanValue(params, 'aviationCommunityDay');
            query.aviationEventsFilter.wingsAndWheels = SearchQuery.getBooleanValue(params, 'wingsAndWheels');
            query.aviationEventsFilter.aircraftDisplayRides = SearchQuery.getBooleanValue(params, 'aircraftDisplayRides');
            query.aviationEventsFilter.aopaRustyPilots = SearchQuery.getBooleanValue(params, 'aopaRustyPilots');
            query.aviationEventsFilter.aopaFlyingClub = SearchQuery.getBooleanValue(params, 'aopaFlyingClub');
            query.aviationEventsFilter.safetySeminar = SearchQuery.getBooleanValue(params, 'safetySeminar');
            query.aviationEventsFilter.conference = SearchQuery.getBooleanValue(params, 'conference');
            query.aviationEventsFilter.workshop = SearchQuery.getBooleanValue(params, 'workshop');
            query.aviationEventsFilter.aopaBacktoYourRoots = SearchQuery.getBooleanValue(params, 'aopaBacktoYourRoots');
            query.aviationEventsFilter.formatOnline = SearchQuery.getBooleanValue(params, 'formatOnline');
            query.aviationEventsFilter.formatInPerson = SearchQuery.getBooleanValue(params, 'formatInPerson');
            if (params.has('name')) {
                query.aviationEventsFilter.name = params.get('name');
            }
            if (params.has('startDate')) {
                var tempStartDate = moment(params.get('startDate'));
                query.aviationEventsFilter.startDate = tempStartDate.toDate();
            }
            if (params.has('endDate')) {
                var tempEndDate = moment(params.get('endDate'));
                query.aviationEventsFilter.endDate = tempEndDate.toDate();
            }
        }
        if (params.get('t') === 'flyingclubs') {
            query.type = SearchFormType.FlyingClubs;
            query.flyingClubFinderFilter = new FlyingClubFinderFilter();
            if (params.has('fcName')) {
                query.flyingClubFinderFilter.name = params.get('fcName');
            }
            query.flyingClubFinderFilter.membership = SearchQuery.parseList(params.get('fcMembership'));
            query.flyingClubFinderFilter.benefits = SearchQuery.parseList(params.get('fcBenefits'));
            query.flyingClubFinderFilter.flying = SearchQuery.parseList(params.get('fcFlying'));
            query.flyingClubFinderFilter.aopaNetwork = SearchQuery.getBooleanValue(params, 'fcAopaNetwork');
            query.flyingClubFinderFilter.inFormation = SearchQuery.getBooleanValue(params, 'fcInFormation');
            query.flyingClubFinderFilter.equityClub = SearchQuery.getBooleanValue(params, 'fcEquity');
            query.flyingClubFinderFilter.nonEquityClub = SearchQuery.getBooleanValue(params, 'fcNonEquity');
        }
        if (params.get('t') === 'flightschools') {
            query.type = SearchFormType.FlightSchools;
            query.flightSchoolFinderFilter = new FlightSchoolFinderFilter();
            if (params.has('fsName')) {
                query.flightSchoolFinderFilter.name = params.get('fsName');
            }
            query.flightSchoolFinderFilter.classification = SearchQuery.parseList(params.get('fsClassification'));
            query.flightSchoolFinderFilter.certifications = SearchQuery.parseList(params.get('fsCertifications'));
            query.flightSchoolFinderFilter.ratings = SearchQuery.parseList(params.get('fsRatings'));
            query.flightSchoolFinderFilter.advanced = SearchQuery.parseList(params.get('fsAdvanced'));
            query.flightSchoolFinderFilter.services = SearchQuery.parseList(params.get('fsServices'));
            query.flightSchoolFinderFilter.other = SearchQuery.parseList(params.get('fsOther'));
        }
        if (params.get('t') === 'universities') {
            query.type = SearchFormType.Universities;
            query.universityFinderFilter = new UniversityFinderFilter();
            if (params.has('uName')) {
                query.universityFinderFilter.name = params.get('uName');
            }
            query.universityFinderFilter.degrees = SearchQuery.parseList(params.get('uDegrees'));
            query.universityFinderFilter.majors = SearchQuery.parseList(params.get('uMajors'));
            query.universityFinderFilter.accreditation = SearchQuery.parseList(params.get('uAccreditation'));
            query.universityFinderFilter.options = SearchQuery.parseList(params.get('uOptions'));
        }
        if (params.get('t') === 'fbos') {
            query.type = SearchFormType.Fbos;
            query.fboFinderFilter = new FboFinderFilter();
            if (params.has('fboName')) {
                query.fboFinderFilter.name = params.get('fboName');
            }
            query.fboFinderFilter.amenities = SearchQuery.parseList(params.get('fboAmenities'), '|');
            query.fboFinderFilter.facilities = SearchQuery.parseList(params.get('fboFacilities'), '|');
            query.fboFinderFilter.fuel = SearchQuery.parseList(params.get('fboFuel'), '|');
            query.fboFinderFilter.fuelType = SearchQuery.parseList(params.get('fboFuelType'), '|');
            query.fboFinderFilter.lineServices = SearchQuery.parseList(params.get('fboLineServices'), '|');
            query.fboFinderFilter.rentalCar = SearchQuery.parseList(params.get('fboRentalCar'), '|');
            query.fboFinderFilter.transportation = SearchQuery.parseList(params.get('fboTransportation'), '|');
        }
        if (params.get('t') === 'servicerepair') {
            query.type = SearchFormType.ServiceRepair;
            query.serviceRepairFinderFilter = new ServiceRepairFinderFilter();
            if (params.has('srName')) {
                query.serviceRepairFinderFilter.name = params.get('srName');
            }
            query.serviceRepairFinderFilter.airframe = SearchQuery.parseList(params.get('srAirframe'), '|');
            query.serviceRepairFinderFilter.airframeManufacturers = SearchQuery.parseList(params.get('srAirframeMfr'), '|');
            query.serviceRepairFinderFilter.avionics = SearchQuery.parseList(params.get('srAvionics'), '|');
            query.serviceRepairFinderFilter.avionicsManufacturers = SearchQuery.parseList(params.get('srAvionicsMfr'), '|');
            query.serviceRepairFinderFilter.powerplant = SearchQuery.parseList(params.get('srPowerplant'), '|');
            query.serviceRepairFinderFilter.powerplantManufacturers = SearchQuery.parseList(params.get('srPowerplantMfr'), '|');
            query.serviceRepairFinderFilter.propeller = SearchQuery.parseList(params.get('srPropeller'), '|');
            query.serviceRepairFinderFilter.propellerManufacturers = SearchQuery.parseList(params.get('srPropellerMfr'), '|');
            query.serviceRepairFinderFilter.interior = SearchQuery.parseList(params.get('srInterior'), '|');
            query.serviceRepairFinderFilter.exterior = SearchQuery.parseList(params.get('srExterior'), '|');
            query.serviceRepairFinderFilter.rotor = SearchQuery.parseList(params.get('srRotor'), '|');
        }
        if (params.get('t') === 'rentalsales') {
            query.type = SearchFormType.RentalSales;
            query.rentalSalesFinderFilter = new RentalSalesFinderFilter();
            if (params.has('rsName')) {
                query.rentalSalesFinderFilter.name = params.get('rsName');
            }
            query.rentalSalesFinderFilter.aircraftRental = SearchQuery.parseList(params.get('rsAircraftRental'), '|');
            query.rentalSalesFinderFilter.salesTypes = SearchQuery.parseList(params.get('rsSalesTypes'), '|');
            query.rentalSalesFinderFilter.aircraftSalesManufacturers = SearchQuery.parseList(params.get('rsAircraftSalesMfr'), '|');
            query.rentalSalesFinderFilter.avionicsSalesManufacturers = SearchQuery.parseList(params.get('rsAvionicsSalesMfr'), '|');
        }
        if (params.get('t') === 'fuel') {
            query.type = SearchFormType.FuelPrice;
        }
        query.filters.forEach(function (filter) {
            var value;
            value = filter.multi ? params.getAll(filter.key) : params.get(filter.key);
            filter.enabled = filter.decode(value);
        });
        return query;
    };
    /**
     * Encode the search query as a set of URL parameters
     * @returns {Params}
     */
    SearchQuery.prototype.encode = function () {
        var params = {};
        if (this.term) {
            params['q'] = this.term;
        }
        if (this.location && this.radius) {
            params['loc'] = SearchQuery.formatLatLon(this.location);
            params['radius'] = this.radius;
            if (this.locationDescription) {
                params['locstr'] = this.locationDescription;
            }
        }
        else if (this.bounds) {
            params['ne'] = SearchQuery.formatLatLon(this.bounds.ne);
            params['sw'] = SearchQuery.formatLatLon(this.bounds.sw);
        }
        else if (this.state) {
            params['state'] = this.state;
            if (this.locationDescription) {
                params['locstr'] = this.locationDescription;
            }
        }
        if (this.type === SearchFormType.Activities) {
            params['t'] = 'activities';
            params['activity'] = this.activity;
        }
        if (this.type === SearchFormType.AviationEvents) {
            params['t'] = 'events';
            params['airshow'] = this.aviationEventsFilter.airshow;
            params['airshowwithMilitaryDemonstrationTeam'] = this.aviationEventsFilter.airshowwithmilitarydemonstrationteam;
            params['flyin'] = this.aviationEventsFilter.flyin;
            params['flyout'] = this.aviationEventsFilter.flyout;
            params['pancakeBreakfast'] = this.aviationEventsFilter.pancakeBreakfast;
            params['socialGathering'] = this.aviationEventsFilter.socialGathering;
            params['openhouse'] = this.aviationEventsFilter.openhouse;
            params['pokerRun'] = this.aviationEventsFilter.pokerRun;
            params['aviationCommunityDay'] = this.aviationEventsFilter.aviationCommunityDay;
            params['wingsAndWheels'] = this.aviationEventsFilter.wingsAndWheels;
            params['aircraftDisplayRides'] = this.aviationEventsFilter.aircraftDisplayRides;
            params['aopaRustyPilots'] = this.aviationEventsFilter.aopaRustyPilots;
            params['aopaFlyingClub'] = this.aviationEventsFilter.aopaFlyingClub;
            params['safetySeminar'] = this.aviationEventsFilter.safetySeminar;
            params['conference'] = this.aviationEventsFilter.conference;
            params['workshop'] = this.aviationEventsFilter.workshop;
            params['aopaBacktoYourRoots'] = this.aviationEventsFilter.aopaBacktoYourRoots;
            params['formatOnline'] = this.aviationEventsFilter.formatOnline;
            params['formatInPerson'] = this.aviationEventsFilter.formatInPerson;
            params['name'] = this.aviationEventsFilter.name;
            params['startDate'] = moment(this.aviationEventsFilter.startDate).local().format('YYYY-MM-DD');
            params['endDate'] = moment(this.aviationEventsFilter.endDate).local().format('YYYY-MM-DD');
        }
        if (this.type === SearchFormType.FlyingClubs) {
            params['t'] = 'flyingclubs';
            if (this.flyingClubFinderFilter) {
                if (this.flyingClubFinderFilter.name) {
                    params['fcName'] = this.flyingClubFinderFilter.name;
                }
                if (this.flyingClubFinderFilter.membership.length) {
                    params['fcMembership'] = this.flyingClubFinderFilter.membership.join(',');
                }
                if (this.flyingClubFinderFilter.benefits.length) {
                    params['fcBenefits'] = this.flyingClubFinderFilter.benefits.join(',');
                }
                if (this.flyingClubFinderFilter.flying.length) {
                    params['fcFlying'] = this.flyingClubFinderFilter.flying.join(',');
                }
                params['fcAopaNetwork'] = this.flyingClubFinderFilter.aopaNetwork;
                params['fcInFormation'] = this.flyingClubFinderFilter.inFormation;
                params['fcEquity'] = this.flyingClubFinderFilter.equityClub;
                params['fcNonEquity'] = this.flyingClubFinderFilter.nonEquityClub;
            }
        }
        if (this.type === SearchFormType.FlightSchools) {
            params['t'] = 'flightschools';
            if (this.flightSchoolFinderFilter) {
                if (this.flightSchoolFinderFilter.name) {
                    params['fsName'] = this.flightSchoolFinderFilter.name;
                }
                if (this.flightSchoolFinderFilter.classification.length) {
                    params['fsClassification'] = this.flightSchoolFinderFilter.classification.join(',');
                }
                if (this.flightSchoolFinderFilter.certifications.length) {
                    params['fsCertifications'] = this.flightSchoolFinderFilter.certifications.join(',');
                }
                if (this.flightSchoolFinderFilter.ratings.length) {
                    params['fsRatings'] = this.flightSchoolFinderFilter.ratings.join(',');
                }
                if (this.flightSchoolFinderFilter.advanced.length) {
                    params['fsAdvanced'] = this.flightSchoolFinderFilter.advanced.join(',');
                }
                if (this.flightSchoolFinderFilter.services.length) {
                    params['fsServices'] = this.flightSchoolFinderFilter.services.join(',');
                }
                if (this.flightSchoolFinderFilter.other.length) {
                    params['fsOther'] = this.flightSchoolFinderFilter.other.join(',');
                }
            }
        }
        if (this.type === SearchFormType.Universities) {
            params['t'] = 'universities';
            if (this.universityFinderFilter) {
                if (this.universityFinderFilter.name) {
                    params['uName'] = this.universityFinderFilter.name;
                }
                if (this.universityFinderFilter.degrees.length) {
                    params['uDegrees'] = this.universityFinderFilter.degrees.join(',');
                }
                if (this.universityFinderFilter.majors.length) {
                    params['uMajors'] = this.universityFinderFilter.majors.join(',');
                }
                if (this.universityFinderFilter.accreditation.length) {
                    params['uAccreditation'] = this.universityFinderFilter.accreditation.join(',');
                }
                if (this.universityFinderFilter.options.length) {
                    params['uOptions'] = this.universityFinderFilter.options.join(',');
                }
            }
        }
        if (this.type === SearchFormType.Fbos) {
            params['t'] = 'fbos';
            if (this.fboFinderFilter) {
                if (this.fboFinderFilter.name) {
                    params['fboName'] = this.fboFinderFilter.name;
                }
                // Joined with '|' rather than ',' because some FBO tag values contain a comma (e.g. "Universal Fuels, Inc").
                if (this.fboFinderFilter.amenities.length) {
                    params['fboAmenities'] = this.fboFinderFilter.amenities.join('|');
                }
                if (this.fboFinderFilter.facilities.length) {
                    params['fboFacilities'] = this.fboFinderFilter.facilities.join('|');
                }
                if (this.fboFinderFilter.fuel.length) {
                    params['fboFuel'] = this.fboFinderFilter.fuel.join('|');
                }
                if (this.fboFinderFilter.fuelType.length) {
                    params['fboFuelType'] = this.fboFinderFilter.fuelType.join('|');
                }
                if (this.fboFinderFilter.lineServices.length) {
                    params['fboLineServices'] = this.fboFinderFilter.lineServices.join('|');
                }
                if (this.fboFinderFilter.rentalCar.length) {
                    params['fboRentalCar'] = this.fboFinderFilter.rentalCar.join('|');
                }
                if (this.fboFinderFilter.transportation.length) {
                    params['fboTransportation'] = this.fboFinderFilter.transportation.join('|');
                }
            }
        }
        if (this.type === SearchFormType.ServiceRepair) {
            params['t'] = 'servicerepair';
            if (this.serviceRepairFinderFilter) {
                var sr = this.serviceRepairFinderFilter;
                if (sr.name) {
                    params['srName'] = sr.name;
                }
                // Joined with '|' rather than ',' because some Maintenance tag values contain a comma
                // (e.g. "Large Aircraft (>12,500 lbs)").
                if (sr.airframe.length) {
                    params['srAirframe'] = sr.airframe.join('|');
                }
                if (sr.airframeManufacturers.length) {
                    params['srAirframeMfr'] = sr.airframeManufacturers.join('|');
                }
                if (sr.avionics.length) {
                    params['srAvionics'] = sr.avionics.join('|');
                }
                if (sr.avionicsManufacturers.length) {
                    params['srAvionicsMfr'] = sr.avionicsManufacturers.join('|');
                }
                if (sr.powerplant.length) {
                    params['srPowerplant'] = sr.powerplant.join('|');
                }
                if (sr.powerplantManufacturers.length) {
                    params['srPowerplantMfr'] = sr.powerplantManufacturers.join('|');
                }
                if (sr.propeller.length) {
                    params['srPropeller'] = sr.propeller.join('|');
                }
                if (sr.propellerManufacturers.length) {
                    params['srPropellerMfr'] = sr.propellerManufacturers.join('|');
                }
                if (sr.interior.length) {
                    params['srInterior'] = sr.interior.join('|');
                }
                if (sr.exterior.length) {
                    params['srExterior'] = sr.exterior.join('|');
                }
                if (sr.rotor.length) {
                    params['srRotor'] = sr.rotor.join('|');
                }
            }
        }
        if (this.type === SearchFormType.RentalSales) {
            params['t'] = 'rentalsales';
            if (this.rentalSalesFinderFilter) {
                var rs = this.rentalSalesFinderFilter;
                if (rs.name) {
                    params['rsName'] = rs.name;
                }
                // Joined with '|' rather than ',' for consistency with the other tag-facet finders (some tag values
                // contain a comma).
                if (rs.aircraftRental.length) {
                    params['rsAircraftRental'] = rs.aircraftRental.join('|');
                }
                if (rs.salesTypes.length) {
                    params['rsSalesTypes'] = rs.salesTypes.join('|');
                }
                if (rs.aircraftSalesManufacturers.length) {
                    params['rsAircraftSalesMfr'] = rs.aircraftSalesManufacturers.join('|');
                }
                if (rs.avionicsSalesManufacturers.length) {
                    params['rsAvionicsSalesMfr'] = rs.avionicsSalesManufacturers.join('|');
                }
            }
        }
        if (this.type === SearchFormType.FuelPrice) {
            params['t'] = 'fuel';
        }
        this.filters
            .filter(function (f) { return f.enabled && f.valid; })
            .forEach(function (filter) {
            if (filter.multi) {
                var param = params[filter.key] || [];
                var value = filter.encode();
                if (!isNullOrUndefined(value)) {
                    param.push(value);
                    params[filter.key] = param;
                }
            }
            else {
                var value = filter.encode();
                if (!isNullOrUndefined(value)) {
                    params[filter.key] = value;
                }
            }
        });
        return params;
    };
    /**
     * Returns a filter (or array of filters, if the multi flag is true) for a given filter key.
     * @param {FilterKeys} key
     * @returns {Filter | Array<Filter>}
     */
    SearchQuery.prototype.getFilter = function (key) {
        return this._filterMap.get(key);
    };
    /**
     * Validates the search query to ensure a minimum amount of information has been entered to execute a search, and
     * that no conflicting parameters were entered.
     * @returns {QueryValidationResult}
     */
    SearchQuery.prototype.validate = function () {
        var term = this.sanitizedTerm;
        // A Flying Club search may be initiated with just a club name (no location); it is matched server-side
        // against the club name / associated airport ident, so treat it as a valid base parameter.
        var clubKeyword = (this.type === SearchFormType.FlyingClubs && this.flyingClubFinderFilter)
            ? AirportService.sanitizeSearchTerm(this.flyingClubFinderFilter.name) : '';
        var hasClubKeyword = clubKeyword.length >= 3;
        // A Flight School search may likewise be initiated with just a school name (no location); it is matched
        // server-side against the school name / associated airport ident.
        var schoolKeyword = (this.type === SearchFormType.FlightSchools && this.flightSchoolFinderFilter)
            ? AirportService.sanitizeSearchTerm(this.flightSchoolFinderFilter.name) : '';
        var hasSchoolKeyword = schoolKeyword.length >= 3;
        // A University search may likewise be initiated with just a university name (no location); it is matched
        // server-side against the university name / associated airport ident.
        var universityKeyword = (this.type === SearchFormType.Universities && this.universityFinderFilter)
            ? AirportService.sanitizeSearchTerm(this.universityFinderFilter.name) : '';
        var hasUniversityKeyword = universityKeyword.length >= 3;
        // An FBO (Business Finder) search may likewise be initiated with just a business name (no location); it is
        // matched server-side against the business name / associated airport ident.
        var fboKeyword = (this.type === SearchFormType.Fbos && this.fboFinderFilter)
            ? AirportService.sanitizeSearchTerm(this.fboFinderFilter.name) : '';
        var hasFboKeyword = fboKeyword.length >= 3;
        // A Service/Repair search may likewise be initiated with just a business name (no location); it is matched
        // server-side against the business name / associated airport ident.
        var serviceRepairKeyword = (this.type === SearchFormType.ServiceRepair && this.serviceRepairFinderFilter)
            ? AirportService.sanitizeSearchTerm(this.serviceRepairFinderFilter.name) : '';
        var hasServiceRepairKeyword = serviceRepairKeyword.length >= 3;
        // A Rentals/Sales search may likewise be initiated with just a business name (no location); it is matched
        // server-side against the business name / associated airport ident.
        var rentalSalesKeyword = (this.type === SearchFormType.RentalSales && this.rentalSalesFinderFilter)
            ? AirportService.sanitizeSearchTerm(this.rentalSalesFinderFilter.name) : '';
        var hasRentalSalesKeyword = rentalSalesKeyword.length >= 3;
        // Validate search type
        if (!Object.values(SearchFormType).includes(this.type)) {
            return {
                valid: false,
                reason: QueryValidationReason.INVALID_SEARCH_TYPE
            };
        }
        // Validate we either have query text, a lat/lon, or bounding box
        // allow searching for US events without a search term
        // allow searching for universities nationwide without a location — there are few enough of them (~300)
        // that an unscoped search is intentional (see UniversityManager.GetUniversities)
        if ((((!term || term.length < 3) && this.type !== SearchFormType.AviationEvents
            && this.type !== SearchFormType.Universities) ||
            ((!term || term.length < 3) && (this.type === SearchFormType.AviationEvents && this.aviationEventsFilter
                && this.aviationEventsFilter.international))) && !this.location && !this.bounds && !this.state && !hasClubKeyword && !hasSchoolKeyword && !hasUniversityKeyword && !hasFboKeyword && !hasServiceRepairKeyword && !hasRentalSalesKeyword) {
            return {
                valid: false,
                reason: QueryValidationReason.MISSING_BASE_PARAMS,
                message: 'Please enter at least three characters or select a valid location to initiate a search.'
            };
        }
        if (((term && term.length > 0 && this.type === SearchFormType.AviationEvents) ||
            (term && term.length > 0 && this.type === SearchFormType.FuelPrice)) && !this.location && !this.bounds && !this.state) {
            return {
                valid: false,
                reason: QueryValidationReason.MISSING_BASE_PARAMS,
                message: 'Please choose a search location from the dropdown list.'
            };
        }
        // If doing an activity search, location or bounding box is required
        if (this.type === SearchFormType.Activities && !this.location && !this.bounds) {
            return {
                valid: false,
                reason: QueryValidationReason.MISSING_BASE_PARAMS,
                message: 'Please select a location from the dropdown to initiate an activity search.'
            };
        }
        // If we have a lat/lon, distance is required
        if (this.location && !this.radius) {
            return {
                valid: false,
                reason: QueryValidationReason.MISSING_RADIUS
            };
        }
        // Validate radius is in range
        if (this.type === SearchFormType.AviationEvents && this.radius && (this.radius < MIN_RADIUS || this.radius > 500)) {
            return {
                valid: false,
                reason: QueryValidationReason.INVALID_RADIUS,
                message: "Distance must be between " + MIN_RADIUS + " and " + MAX_RADIUS + " nautical miles."
            };
        }
        if (this.type !== SearchFormType.AviationEvents && this.radius && (this.radius < MIN_RADIUS || this.radius > MAX_RADIUS)) {
            return {
                valid: false,
                reason: QueryValidationReason.INVALID_RADIUS,
                message: "Distance must be between " + MIN_RADIUS + " and " + MAX_RADIUS + " nautical miles."
            };
        }
        // Ensure all filters are valid
        var invalid = this.filters
            .filter(function (f) { return f.enabled; })
            .map(function (f) { return f.validate(); })
            .filter(function (r) { return !r.valid; });
        if (invalid.length) {
            return invalid[0];
        }
        return { valid: true };
    };
    /**
     * Execute the query against a list of airport search results.
     * @param airports {Array<AirportSearchResult>} Airport search results to filter
     * @returns {AirportSearchResult[]} Airport search results that match the filters
     */
    SearchQuery.prototype.execute = function (airports) {
        var _this = this;
        return airports.filter(function (airport) {
            return _this.filters.every(function (filter) { return filter.apply(airport); });
        });
    };
    return SearchQuery;
}());
export { SearchQuery };
